import type { StorageLike } from "./storage-adapter";

/**
 * Real play-frequency accounting for external (Rosebud) launcher tiles.
 *
 * Deliberately a separate store from `ArcadeStateV1.games` (schema.ts), not
 * an extension of it: that schema is scoped to first-party `GameRegistry`
 * entries, and external links open a framed Rosebud session, not a hosted
 * game. Mixing the two would blur exactly the first-party/external
 * accounting boundary root rule 20 keeps for system surfaces. A tap here is
 * a launcher click, not a played match -- Rosebud gives no signal back once
 * the game is framed.
 */

const EXTERNAL_TAPS_STORAGE_KEY = "tesla-arcade:external-taps:v1";
// Defensive bound only; the reviewed catalog is currently 56 entries and a
// refresh per wiki/product/rosebud-catalog.md stays in the low hundreds.
const MAX_TRACKED_SLUGS = 2_000;

export type ExternalTapCounts = Readonly<Record<string, number>>;

function isTapCounts(value: unknown): value is Record<string, number> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.values(value as Record<string, unknown>).every(
    (count) => Number.isInteger(count) && (count as number) >= 0,
  );
}

export function loadExternalTapCounts(storage: StorageLike | undefined): ExternalTapCounts {
  if (!storage) return {};
  try {
    const raw = storage.getItem(EXTERNAL_TAPS_STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return isTapCounts(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function recordExternalTap(storage: StorageLike | undefined, slug: string): void {
  if (!storage) return;
  try {
    const counts = { ...loadExternalTapCounts(storage) };
    if (!(slug in counts) && Object.keys(counts).length >= MAX_TRACKED_SLUGS) return;
    counts[slug] = (counts[slug] ?? 0) + 1;
    storage.setItem(EXTERNAL_TAPS_STORAGE_KEY, JSON.stringify(counts));
  } catch {
    // Tap accounting is a convenience; opening the overlay continues
    // regardless (this runs synchronously in the click handler, alongside
    // opening the external-game overlay).
  }
}
